import React from 'react';
import { Text, Button, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { Board20Regular, ArrowRight16Regular } from '@fluentui/react-icons';
import type { BusinessDatasetItem } from '../../types/discovery';
import { MutationButton, useWorkspaceAccess } from '../security/WorkspaceAccess';

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  headerArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '14px',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    ...shorthands.padding('12px', '14px'),
    gap: '5px',
    minHeight: '220px',
    maxHeight: '260px',
    transitionProperty: 'box-shadow, border-color, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
    ':hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
      ...shorthands.borderColor(tokens.colorNeutralStroke1),
      transform: 'translateY(-1px)',
    },
  },
  title: {
    fontSize: '13px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    lineHeight: '18px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '15px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  chip: {
    fontSize: '9px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('6px'),
    ...shorthands.padding('1px', '7px'),
  },
  crossDomainBadge: {
    fontSize: '9px',
    fontWeight: 600,
    color: tokens.colorPaletteBerryForeground1,
    backgroundColor: tokens.colorPaletteBerryBackground2,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('1px', '5px'),
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },
  questionsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginTop: '2px',
  },
  questionsLabel: {
    fontSize: '9px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: tokens.colorNeutralForeground4,
  },
  questionItem: {
    fontSize: '10px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '::before': {
      content: '"•  "',
      color: tokens.colorNeutralForeground4,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '4px',
    marginTop: 'auto',
  },
  exploreLink: {
    fontSize: '11px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
});

interface Props {
  recommended: BusinessDatasetItem[];
  secondary: BusinessDatasetItem[];
  advanced: BusinessDatasetItem[];
  onOpen: (viewId: string) => void;
  onCreateDashboard?: (viewId: string, prompt: string) => void;
}

const BusinessDatasetsSection: React.FC<Props> = ({
  recommended,
  secondary,
  advanced,
  onOpen,
  onCreateDashboard,
}) => {
  const styles = useStyles();
  const { runMutation } = useWorkspaceAccess();
  const allItems = [...recommended, ...secondary, ...advanced];
  const count = allItems.length;

  if (count === 0) return null;

  return (
    <div className={styles.section}>
      <div className={styles.headerArea}>
        <Text className={styles.sectionTitle}>More Dashboard Ideas</Text>
        <Text className={styles.subtitle}>Additional dashboards you can create from this model</Text>
      </div>

      <div className={styles.grid}>
        {allItems.map((item) => (
          <div key={item.id} className={styles.card}>
            <Text className={styles.title}>{item.name}</Text>
            {item.isCrossDomain && <span className={styles.crossDomainBadge}>Cross-Domain</span>}

            {item.description && (
              <Text className={styles.description}>{item.description}</Text>
            )}

            {item.entityChips.length > 0 && (
              <div className={styles.chipsRow}>
                {item.entityChips.map((chip) => (
                  <span key={chip} className={styles.chip}>{chip}</span>
                ))}
              </div>
            )}

            {item.questions.length > 0 && (
              <div className={styles.questionsSection}>
                <Text className={styles.questionsLabel}>Key Questions</Text>
                {item.questions.map((q, i) => (
                  <Text key={i} className={styles.questionItem}>{q}</Text>
                ))}
              </div>
            )}

            <div className={styles.actions}>
              <MutationButton
                size="small"
                appearance="primary"
                icon={<Board20Regular />}
                onClick={() => runMutation(() => onCreateDashboard?.(item.viewId, item.dashboardPrompt))}
              >
                Create Dashboard
              </MutationButton>
              <Button
                appearance="transparent"
                size="small"
                icon={<ArrowRight16Regular />}
                iconPosition="after"
                onClick={() => onOpen(item.viewId)}
                className={styles.exploreLink}
              >
                Explore
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BusinessDatasetsSection;